
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {ChartInfoFull} from './ChartInfoFull';
import {FieldModelChartInfoFull} from './ChartInfoFull';

/**
 * ChartInfoGetResponse struct
 */
class ChartInfoGetResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!ChartInfoFull=} chartInfoFull
   * @constructor
   */
  constructor (parent = new Response(), chartInfoFull = new ChartInfoFull()) {
    super()
    super.copy(parent)
    this.ChartInfoFull = chartInfoFull
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartInfoGetResponse}
   * @param {!ChartInfoGetResponse} other Other struct
   * @returns {!ChartInfoGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.ChartInfoFull != null) {
      this.ChartInfoFull = ChartInfoFull.fromObject(other.ChartInfoFull)
    } else {
      this.ChartInfoFull = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartInfoGetResponse}
   * @returns {!ChartInfoGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartInfoGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartInfoGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartInfoGetResponse}
   * @param {!ChartInfoGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartInfoGetResponse)) {
      throw new TypeError('Instance of ChartInfoGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartInfoGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      ChartInfoFull: ((this.ChartInfoFull != null) ? this.ChartInfoFull : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartInfoGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartInfoGetResponse} other Object value
   * @returns {!ChartInfoGetResponse} Created struct
   */
  static fromObject (other) {
    return new ChartInfoGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartInfoGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartInfoGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartInfoGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5200
  }
}

export { ChartInfoGetResponse };

/**
 * Fast Binary Encoding ChartInfoGetResponse field model
 */
class FieldModelChartInfoGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.ChartInfoFull = new FieldModelChartInfoFull(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.ChartInfoFull.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.ChartInfoFull.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartInfoGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5200
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ChartInfoFull.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartInfoFull.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartInfoFull.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!ChartInfoGetResponse} fbeValue Default value, defaults is new ChartInfoGetResponse()
   * @returns {!ChartInfoGetResponse} ChartInfoGetResponse value
   */
  get (fbeValue = new ChartInfoGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!ChartInfoGetResponse} fbeValue ChartInfoGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ChartInfoFull.fbeSize) <= fbeStructSize) {
      fbeValue.ChartInfoFull = this.ChartInfoFull.get()
    } else {
      fbeValue.ChartInfoFull = new ChartInfoFull()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartInfoFull.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartInfoGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartInfoGetResponse}
   * @param {!ChartInfoGetResponse} fbeValue ChartInfoGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartInfoGetResponse}
   * @param {ChartInfoGetResponse} fbeValue ChartInfoGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.ChartInfoFull.set(fbeValue.ChartInfoFull)
  }
}

export { FieldModelChartInfoGetResponse };

/**
 * Fast Binary Encoding ChartInfoGetResponse model
 */
class ChartInfoGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartInfoGetResponse(this.buffer, 4)
  }

  /**
   * Get the ChartInfoGetResponse model
   * @this {!ChartInfoGetResponseModel}
   * @returns {!FieldModelChartInfoGetResponse} model ChartInfoGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartInfoGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartInfoGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartInfoGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartInfoGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartInfoGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartInfoGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartInfoGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartInfoGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartInfoGetResponseModel}
   * @param {!ChartInfoGetResponse} value ChartInfoGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartInfoGetResponseModel}
   * @param {!ChartInfoGetResponse} value ChartInfoGetResponse value, defaults is new ChartInfoGetResponse()
   * @return {!object} Deserialized ChartInfoGetResponse value and its size
   */
  deserialize (value = new ChartInfoGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartInfoGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartInfoGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartInfoGetResponseModel}
   * @param {!number} prev Previous ChartInfoGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartInfoGetResponseModel };
